import { Identifiable } from "../core";

export type BroadcastStatus =
  | "draft"
  | "ready"
  | "sending"
  | "paused"
  | "sent"
  | "cancelled";

export type RecipientStatus =
  | "pending"
  /** Claimed by a run and not yet answered for. Short-lived. */
  | "sending"
  | "excluded"
  | "sent"
  | "delivered"
  | "read"
  | "failed";

export type ExcludeReason =
  | "no_phone"
  | "unparseable"
  | "invalid"
  | "duplicate"
  | "manual"
  | "suppressed";

/**
 * Recipients per status, plus `total` and `sendable`.
 *
 * Every status is always present, zeros included — the backend fills them in
 * so a missing key here would be a bug rather than "none of those".
 */
export type BroadcastCounts = Record<RecipientStatus | ExcludeReason, number> & {
  total: number;
  sendable: number;
};

export type TemplateStatus =
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  /** Ours, not Meta's: the template is gone from the account. */
  | "REMOVED";

export type ParameterFormat = "POSITIONAL" | "NAMED";

export interface IWhatsAppTemplate extends Identifiable {
  id: number;
  external_id: string;
  name: string;
  /** `es` and `es_MX` are different templates to the send API, not a pair. */
  language: string;
  status: TemplateStatus;
  category: string;
  /**
   * Decides the wire shape of every parameter. Three of the five templates on
   * the real account are NAMED and two of those use different names, so this
   * can never be assumed from the template name.
   */
  parameter_format: ParameterFormat;
  body_text: string;
  /** Ordered keys the body asks for: `["1","2"]` or `["contact_name"]`. */
  variables: string[];
  examples: Record<string, string>;
  /** `body_text` with Meta's own example values filled in. */
  preview: string;
  is_sendable: boolean;
  button_labels: string[];
  synced_at: string | null;
}

export interface IBroadcast extends Identifiable {
  id: number;
  name: string;
  status: BroadcastStatus;
  is_editable: boolean;
  /**
   * Whose list this is. Every action taken from the broadcast's page inherits
   * it — platform staff can act for several operators, so the server refuses
   * to guess and anything that creates or pulls has to say which one.
   */
  operator: number;
  template: number | null;
  template_detail: IWhatsAppTemplate | null;
  template_name: string;
  template_language: string;
  /** `{"1": "greeting", "2": "column:Email"}` — keyed by Meta's `{{n}}`. */
  variable_map: Record<string, string>;
  source_filename: string;
  created_by: number | null;
  created_by_name: string;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  modified_at: string;
  counts: BroadcastCounts;
}

export interface IBroadcastRecipient extends Identifiable {
  id: number;
  broadcast: number;
  /** 1-based line of the uploaded file, so "row 43" means row 43. */
  row_number: number;
  /** The whole original CSV row, by header. */
  source_row: Record<string, string>;
  name_raw: string;
  greeting: string;
  phone_raw: string;
  phone_e164: string;
  phone_region: string;
  status: RecipientStatus;
  exclude_reason: ExcludeReason | "";
  /** The human phrasing of `exclude_reason`, from the server. */
  exclude_label: string;
  sent_params: string[];
  wa_message_id: string;
  wa_id: string;
  error_code: number | null;
  error: string;
  attempts: number;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
}

/** One row of a not-yet-stored file — `preview` returns these. */
export interface IPreparedRow {
  row_number: number;
  source_row: Record<string, string>;
  name_raw: string;
  greeting: string;
  phone_raw: string;
  phone_e164: string;
  phone_region: string;
  status: RecipientStatus;
  exclude_reason: ExcludeReason | "";
}

export interface IRenderedSample {
  recipient: number;
  greeting: string;
  phone_e164: string;
  params: { key: string; value: string }[];
  /** The whole message, filled in — reading it is what catches a bad mapping. */
  text: string;
  has_empty: boolean;
}

export interface IRendered {
  template_name: string;
  template_language: string;
  parameter_format: ParameterFormat | "";
  samples: IRenderedSample[];
}

export interface ISyncReport {
  added: number;
  updated: number;
  removed: number;
  total: number;
}

export interface IDeliveryStatus {
  /** False when no app secret is set — callbacks are accepted but forgeable. */
  signature_verification: boolean;
  subscription_configured: boolean;
}

export interface IBroadcastPreview {
  headers: string[];
  name_column: string;
  phone_column: string;
  counts: BroadcastCounts;
  /** Sendable rows only, by ISO country code. */
  regions: Record<string, number>;
  rows: IPreparedRow[];
}

export const EXCLUDE_LABELS: Record<ExcludeReason, string> = {
  no_phone: "No phone number",
  unparseable: "Couldn’t read the number",
  invalid: "Not a valid number",
  duplicate: "Duplicate",
  manual: "Removed by hand",
  suppressed: "Opted out",
};

/**
 * Only the reasons worth a tab of their own. `manual` and `suppressed` are
 * deliberately absent: they are things the operator did or the customer did,
 * not problems with the file, and a tab reading "Removed by hand: 0" on every
 * fresh import is noise.
 */
export const FILE_PROBLEM_REASONS: ExcludeReason[] = [
  "no_phone",
  "unparseable",
  "invalid",
  "duplicate",
];

export const TEMPLATE_STATUS_COLORS: Record<TemplateStatus, string> = {
  APPROVED: "green",
  PENDING: "blue",
  REJECTED: "red",
  PAUSED: "orange",
  DISABLED: "default",
  REMOVED: "default",
};

export const STATUS_COLORS: Record<BroadcastStatus, string> = {
  draft: "default",
  ready: "blue",
  sending: "processing",
  paused: "warning",
  sent: "success",
  cancelled: "default",
};
